import { api } from "@/api";
import { H3 } from "@/components/Heading";
import { MailToLink } from "@/components/Link";
import { OrderNarcanCALogo } from "@/components/Logo";
import { translations } from "@/constants/translations";
import { renderInnerHtml } from "@/helpers/dom";
import { useAppNavigate } from "@/routing/useAppNavigate";
import { useStore } from "@/store";
import {
  Button,
  Input,
  Modal,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import React from "react";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { contactDtoToOrder } from "@/api/transforms";
import { useApiMutate } from "@/api/hooks/useApiMutate";
import { getApiErrorMessage } from "@/api/getApiErrorMessage";
import { useCaptchaToken } from "@/components/Captcha/useCaptchaToken";
import { telemetry } from "@/telemetry";
import { getSignInResult } from "@/telemetry/events";

interface Props {
  email: string;
}

export const ValidateCodeForm = observer(({ email }: Props) => {
  const { activeLanguage, orderData, setOrderData, setOrderContactData } =
    useStore();
  const navigate = useAppNavigate();
  const [code, setCode] = useState("");
  const [isCodeResend, setCodeResend] = useState(false);
  const codeAttempts = useRef(0);
  const resendAttempts = useRef(0);
  const language = translations.pages["emergent-account"][activeLanguage];

  const getVerifyCodeToken = useCaptchaToken("verify_code");
  const getResendCodeToken = useCaptchaToken("resend_code");

  const apiValidateCode = useApiMutate({
    mutationFn: async (data: { code: string }) => {
      const captchaToken = await getVerifyCodeToken();
      return api.verifyCode({ ...data, captchaToken });
    },
    onSuccess: (data) => {
      telemetry.trackEvent("emergent_code_submitted", {
        result: "ok",
        attempt: codeAttempts.current,
      });
      setOrderData(contactDtoToOrder(data, orderData));
      setOrderContactData(data);

      navigate({
        to: "/order/details",
      });
    },
    onError: (error) => {
      telemetry.trackEvent("emergent_code_submitted", {
        result: getSignInResult(error.response?.status),
        attempt: codeAttempts.current,
      });
      setCode("");
      toast.error(
        translations.common.errors[activeLanguage].incorrect_verification_code,
      );
    },
  });

  const handleSubmit = () => {
    codeAttempts.current += 1;
    apiValidateCode.mutate({ code });
  };

  const apiResendCode = useApiMutate({
    mutationFn: async (data: { email: string; language: string }) => {
      const captchaToken = await getResendCodeToken();
      return api.verifyEmail({ ...data, captchaToken });
    },
    onSuccess: () => {
      setCodeResend(true);
    },
    onError: (data) => {
      toast.error(getApiErrorMessage(data));
    },
  });

  const handleCodeResend = () => {
    resendAttempts.current += 1;
    telemetry.trackEvent("emergent_code_resent", {
      attempt: resendAttempts.current,
    });
    apiResendCode.mutate({
      email,
      language: activeLanguage,
    });
  };

  const handleClose = () => setCodeResend(false);

  return (
    <>
      <H3>{renderInnerHtml(language.verify_email)}</H3>
      <Text>
        {renderInnerHtml(language.code_sent, [
          <Text
            key="1"
            as="span"
            color="primary-light"
          >
            {email}
          </Text>,
        ])}
      </Text>
      <Input
        maxWidth="312px"
        placeholder={language.enter_code}
        onChange={(e) => setCode(e.target.value)}
      />
      <Button
        onClick={handleSubmit}
        width="120px"
        isLoading={apiValidateCode.isPending}
      >
        {language.verify_button}
      </Button>

      <Text>
        {language.cant_find_email}
        <Text
          as="span"
          color="primary-light"
          cursor="pointer"
          onClick={handleCodeResend}
        >
          {language.click_here_code}
        </Text>
      </Text>

      <Modal
        isOpen={isCodeResend}
        onClose={handleClose}
      >
        <ModalOverlay />
        <ModalContent
          p="16"
          maxWidth="620px"
        >
          <Stack
            spacing="14"
            alignItems="center"
            textAlign="center"
          >
            <OrderNarcanCALogo />
            <Stack spacing="8">
              <Text>
                {renderInnerHtml(language.code_sent_description, [
                  <Text
                    key="1"
                    as="span"
                    color="primary"
                  >
                    {email}
                  </Text>,
                ])}
              </Text>
              <Text>
                {renderInnerHtml(language.check_spam, [
                  <Text
                    key="1"
                    as="span"
                    color="primary"
                    whiteSpace="nowrap"
                  >
                    {language.phone}
                  </Text>,
                  <MailToLink
                    color="primary"
                    key="2"
                  >
                    {language.customer_email}
                  </MailToLink>,
                ])}
              </Text>
            </Stack>
            <Button onClick={handleClose}>{language.go_back}</Button>
          </Stack>
        </ModalContent>
      </Modal>
    </>
  );
});
